import AccordionSummary from '@material-ui/core/AccordionSummary';
import { observer, useLocalStore } from 'mobx-react';
import React, { useContext, useEffect, useRef } from 'react';
import 'react-h5-audio-player/lib/styles.css';
import { TopicsContext } from '../../contexts/Topics';
import { UsersContext } from '../../contexts/Users';
import CommentForm from '../common/CommentForm';
import CompletionPanel from '../common/CompletionPanel';
import Error from '../common/Error';
import EvidenceList from '../common/EvidenceList.js';
import MarkItemChecked from '../common/MarkItemChecked';
import StatusLight from '../common/StatusLight';
import MessageForm from '../messaging/MessageForm';
import * as CSS from './elements/StudentModuleTasks';

const StudentModuleTasks = observer(
  ({ studentId, moduleId, store: evidencingStore }) => {
    const usersStore = useContext(UsersContext);
    const topicsStore = useContext(TopicsContext);

    const commentFormRef = useRef(null);

    const store = useLocalStore(() => ({
      evidenceIdToComment: null,
      taskIdBeingEvidenced: null,
      isAddingEvidence: false,
      taskIdToEvidence: null,
      selectEvidence(evidenceId, taskId, isAddingEvidence = false) {
        store.evidenceIdToComment = evidenceId;
        store.taskIdBeingEvidenced = taskId;
        store.isAddingEvidence = isAddingEvidence;
        store.taskIdToEvidence = null;
      },
      setTaskIdToEvidence(taskId) {
        store.taskIdToEvidence = taskId;
      },
      openTaskIds: [],
      setTaskOpen(taskId, isOpen) {
        store.openTaskIds = isOpen
          ? [...store.openTaskIds, taskId]
          : store.openTaskIds.filter((id) => id !== taskId);
      },
    }));

    useEffect(() => {
      if (store.evidenceIdToComment && commentFormRef.current) {
        commentFormRef.current.scrollIntoView({
          block: 'end',
          inline: 'center',
          behavior: 'smooth',
        });
      }
    }, [store.evidenceIdToComment]);

    const student = usersStore.getUserById(studentId);
    const loggedInUser = usersStore.getLoggedInUser();
    const module = topicsStore.getModuleById(moduleId);
    if (!module) return <Error msg="Module not found" />;

    const tasks = module.getOrderedItems('tasks');

    const completionPercent = student.private.getModuleItemsCompletionPercent(
      tasks,
      'tasks'
    );

    const hasTaskUpdates = module.hasTaskUpdates(student.private, loggedInUser);

    return (
      <CSS.StaffNestedSection data-testid="StudentModuleTasks">
        <CSS.StaffRow>
          <CSS.StaffTabs>
            <CSS.UpdateDot show={false}>
              <CSS.StaffFilterLink
                to={`/staff/users/${studentId}/progress/modules/${moduleId}/learning`}
                isfirst
              >
                Learning
              </CSS.StaffFilterLink>
            </CSS.UpdateDot>
            <CSS.UpdateDot
              show={hasTaskUpdates}
              data-testid={`updates-tasksLink`}
            >
              <CSS.StaffFilterLink
                to={`/staff/users/${studentId}/progress/modules/${moduleId}/tasks`}
              >
                Tasks
              </CSS.StaffFilterLink>
            </CSS.UpdateDot>
          </CSS.StaffTabs>
          <CSS.ProgressContainer>
            <CSS.ProgressBarWithLabel
              data-testid="StudentModuleTasks-ProgressBar"
              completionPercent={completionPercent}
            />
          </CSS.ProgressContainer>
        </CSS.StaffRow>
        <CSS.ItemListContainer>
          <CSS.ItemList>
            {tasks.length ? (
              tasks.map((task) => {
                const status = student.private.getItemStatus(task.id, 'tasks');
                const evidence = student.private.getEvidenceByTaskId(task.id);
                const hasUpdates = student.private.hasUpdatesForUser(
                  task.id,
                  'tasks',
                  loggedInUser
                );
                const isOpen = store.openTaskIds.includes(task.id);
                return (
                  <CSS.ItemContainer key={task.id} isTop={task.index === 0}>
                    <MarkItemChecked
                      student={student}
                      itemId={task.id}
                      collection="tasks"
                      isOpen={isOpen}
                    />
                    <CSS.FullWidthUpdateDot
                      show={hasUpdates}
                      data-testid={`updates-${task.id}`}
                    >
                      <CSS.ItemAccordion
                        expanded={isOpen}
                        onChange={(e, expanded) =>
                          store.setTaskOpen(task.id, expanded)
                        }
                      >
                        <AccordionSummary>
                          <CSS.ItemSummary>
                            <CSS.Circle>
                              <CSS.ItemIndex
                                data-testid={`task-${task.index + 1}`}
                              >
                                {task.index + 1}
                              </CSS.ItemIndex>
                            </CSS.Circle>
                            <CSS.ModuleItemInstructions>
                              {task.instructions}
                            </CSS.ModuleItemInstructions>
                            <StatusLight status={status} itemId={task.id} />
                          </CSS.ItemSummary>
                        </AccordionSummary>
                        <CSS.ItemDetails>
                          <CSS.ListContainer>
                            <CSS.DetailSection>
                              <CSS.DetailSectionHeader>
                                <CSS.DetailSectionTitle>
                                  Evidence
                                </CSS.DetailSectionTitle>
                                {store.taskIdToEvidence !== task.id && (
                                  <CSS.Button
                                    $variant="secondary"
                                    $size="sm"
                                    onClick={() =>
                                      store.setTaskIdToEvidence(task.id)
                                    }
                                  >
                                    Add evidence <CSS.PlusIcon />
                                  </CSS.Button>
                                )}
                              </CSS.DetailSectionHeader>
                              <EvidenceList
                                evidence={evidence}
                                resource="task"
                                onEvidenceClick={(evidenceId) =>
                                  store.selectEvidence(evidenceId, task.id)
                                }
                                taskId={task.id}
                                studentId={studentId}
                                store={evidencingStore}
                                isStaff
                              />
                              {store.taskIdBeingEvidenced === task.id ? (
                                <CommentForm
                                  refProp={commentFormRef}
                                  studentId={student.id}
                                  taskId={store.taskIdBeingEvidenced}
                                  evidenceId={store.evidenceIdToComment}
                                  afterSubmit={() =>
                                    store.selectEvidence(null, null)
                                  }
                                />
                              ) : (
                                store.taskIdToEvidence === task.id && (
                                  <CSS.DetailSection>
                                    <CSS.DetailSectionHeader>
                                      <CSS.DetailSectionTitle as="h4">
                                        Adding evidence for{' '}
                                        {student.displayName}
                                      </CSS.DetailSectionTitle>
                                      <CSS.Button
                                        $variant="secondary"
                                        $size="sm"
                                        onClick={() =>
                                          store.setTaskIdToEvidence(null)
                                        }
                                      >
                                        Cancel adding evidence
                                      </CSS.Button>
                                    </CSS.DetailSectionHeader>
                                    <MessageForm
                                      isStaff={true}
                                      taskId={task.id}
                                      moduleId={moduleId}
                                      userToAddTo={student}
                                    ></MessageForm>
                                  </CSS.DetailSection>
                                )
                              )}
                            </CSS.DetailSection>
                            {/* pinned to the bottom of the screen while scrolling through the
                                evidence, so Mark as complete is always to hand (F9) */}
                            <CSS.StickySignOff>
                              <CSS.DetailSection>
                                <CSS.DetailSectionTitle>
                                  Sign off
                                </CSS.DetailSectionTitle>
                                <CompletionPanel
                                  student={student}
                                  itemId={task.id}
                                  collection="tasks"
                                />
                              </CSS.DetailSection>
                            </CSS.StickySignOff>
                          </CSS.ListContainer>
                        </CSS.ItemDetails>
                      </CSS.ItemAccordion>
                    </CSS.FullWidthUpdateDot>
                  </CSS.ItemContainer>
                );
              })
            ) : (
              <CSS.ItemContainer isTop={true}>
                <CSS.ItemAccordion>
                  <CSS.ItemSummary>
                    <CSS.ModuleItemInstructions>
                      No tasks yet
                    </CSS.ModuleItemInstructions>
                  </CSS.ItemSummary>
                </CSS.ItemAccordion>
              </CSS.ItemContainer>
            )}
          </CSS.ItemList>
        </CSS.ItemListContainer>
      </CSS.StaffNestedSection>
    );
  }
);

export default StudentModuleTasks;
